import { handleResponse } from './handleResponse'
import store from '../store'
import { history } from '../entry'
import { localLogoutUser } from '../reducers/auth/currentUser.actions'
import { hasPermission, PERMISSIONS, PERMISSIONS_STATUS } from '../reducers/auth/currentUser.redux'

// The org's user and tag lists back the agent/tag filter on most pages.
// core-service serves them to users:read holders in full and to anyone with an
// analytics read capability in a slim, filter-only shape; everyone else gets a
// 403. Under 'enforce', answer those callers locally with the empty shape each
// endpoint returns — no request, no 403, no toast. Outside 'enforce'
// hasPermission answers true here, so nothing changes. Only this session's
// loaded permissions decide it: before they arrive the request goes out, so a
// user promoted since the last session isn't handed an empty list.
const USER_LIST_PERMISSIONS = [
  PERMISSIONS.USERS_READ,
  PERMISSIONS.REPORTS_READ,
  PERMISSIONS.QUALITY_READ,
  PERMISSIONS.COACHING_READ,
  PERMISSIONS.LEADERBOARD_READ,
]

const USER_LISTS = [
  { pattern: /\/api\/organizations\/\d+\/users$/, empty: { users: [] } },
  { pattern: /\/api\/organizations\/\d+\/tags$/, empty: [] },
  { pattern: /\/api\/organizations\/\d+\/tags\/categories$/, empty: [] },
]

const getDeniedListResponse = (url, method, state) => {
  if (method !== 'GET') {
    return null
  }

  const { pathname } = new URL(url, window.location.origin)
  const list = USER_LISTS.find(({ pattern }) => pattern.test(pathname))

  if (
    !list ||
    state.currentUser.permissions_status !== PERMISSIONS_STATUS.LOADED ||
    USER_LIST_PERMISSIONS.some((permission) =>
      hasPermission(state.currentUser, state.currentOrganization, permission)
    )
  ) {
    return null
  }

  return list.empty
}

/**
 * fetchingAPI is a wrapper for Balto APIs.
 *
 * It grabs the user data from local storage and passes it into the headers, then handles the
 * response in a separate function.
 *
 * We should not be pulling from local storage, but rather from Redux or API interceptors.
 *
 * @param {string} url The full API endpoint URL
 * @param {string} method One of GET, POST, PUT, PATCH, DELETE
 * @param {Function} dispatch The Redux dispatch method
 * @param {Object} body Optional body for POST, PUT, PATCH requests
 * @param {Function} callback Optional callback function
 * @param {*} details An optional details object for modifying the response handler
 * @param {Boolean} useOwnOrganizationID Whether to set organizationid header to user's org id (as opposed to org id user may be viewing)
 * @param signal
 * @param setContentType An optional boolean to enable/disable setting content type
 * @returns {Promise} Returns a promise that either resolves or rejects based on the response code
 */
export const fetchingAPI = (
  url,
  method,
  dispatch,
  body,
  callback,
  details = {},
  useOwnOrganizationID,
  signal,
  setContentType = true
) => {
  const state = store.getState()
  const { username, organizationid, token, own_organization_id } = state.currentUser

  // If no token exists, don't attempt to hit the endpoint because it will always fail
  if (!token) {
    console.info('Token not found. Logging out user.')
    dispatch(localLogoutUser({ history }))
  }

  const deniedListResponse = getDeniedListResponse(url, method, state)

  if (deniedListResponse) {
    return Promise.resolve(deniedListResponse)
  }

  return fetch(url, {
    method,
    body,
    headers: {
      ...(setContentType && { 'Content-Type': 'application/json' }),
      'auth-type': 'web',
      username,
      organizationid: useOwnOrganizationID ? own_organization_id : organizationid,
      token,
    },
    signal,
  }).then((response) => handleResponse(response, dispatch, callback, details))
}

// NOTE: unlike fetchingAPI, this returns the raw response and leaves handling the response to the caller
export const fetchingAPIWithPendingToken = (url, method, dispatch, body) => {
  const state = store.getState()
  const { pendingToken, organizationID, username } = state.mfa

  // If no pendingToken exists, don't attempt to hit the endpoint because it will always fail
  if (!pendingToken) {
    console.info('Pending token not found. Logging out user.')
    dispatch(localLogoutUser({ history }))
  }

  return fetch(url, {
    method,
    body,
    headers: {
      'auth-type': 'web',
      'Content-Type': 'application/json',
      username,
      organizationID,
      pendingToken,
    },
  })
}
